const calculateLocationLedgerQuery = `
  /*
    We start by getting the latest full report to serve as a base for our ledger,
    since client full counts are seen as a source of truth, we set a default date for locations without counts
  */
WITH latest_stock_count AS (
  (
    SELECT
      s.stock_count_id,
      s.submitted_at,
      s.updated_at
    FROM avocado.data_stockcount s
    WHERE s.fs_id = $1
      AND s.partial_count = false
      AND s.submitted_at <= $2
    ORDER BY s.submitted_at DESC
    LIMIT 1
  )
  UNION ALL
  (
    SELECT
      NULL AS stock_count_id,
      '1970-01-01'::timestamptz AS submitted_at,
      NULL::timestamptz AS updated_at
  )
  LIMIT 1
),
  /*
    We then get all count events that happened after that full report
  */
  count_events AS (
    SELECT
      sl.sku,
      -- In some cases delivery sale events are out of sync with the delivery
      -- And so their opening and closing balances include subsequnet deliveries
      -- In those cases if the closing + buyout is less than the calculated partner balance
      -- The closing balance is equal to the partner balance
      closing,
      coalesce(sl.partner_balance, 0) - coalesce(sl.partner_buyout, 0) AS partner_balance, -- Take buyouts into account as well
      sc.submitted_at as event_date,
      sc.fs_id
    FROM avocado.data_stockcount sc
    JOIN avocado.data_stockcountline sl ON sl.stock_count_id = sc.id
    WHERE sc.fs_id = $1
      AND sc.submitted_at >= (SELECT submitted_at FROM latest_stock_count)
      AND sc.submitted_at <= $2
  ),
  /*
    We then also get all shipments (sent and received that happened after the full report,
    making sure to translate skus when necessary
  */
  shipments_sent AS (
    SELECT
      origin_sku as sku,
      -dsl.amount AS closing,
      0 as partner_balance,
      coalesce(dss.created_at, ds.created_at) AS event_date,
      ds.origin_id AS fs_id,
      ds.shipment_id
    FROM avocado.data_shipment ds
    JOIN avocado.data_shipmentline dsl ON dsl.shipment_id = ds.id
    LEFT JOIN avocado.data_shipmentsnapshot dss ON dss.shipment_id = ds.shipment_id AND dss.status = 'sent'
    WHERE ds.origin_id = $1
      AND ds.status IN ('sent', 'arrived', 'received')
      AND coalesce(dss.created_at, ds.created_at) > (SELECT submitted_at FROM latest_stock_count)
      AND coalesce(dss.created_at, ds.created_at) <= $2
  ),
  shipments_received AS (
    SELECT
      destination_sku as sku,
      dsl.amount AS closing,
      0 as partner_balance,
      ds.updated_at AS event_date,
      ds.destination_id AS fs_id,
      ds.shipment_id
    FROM avocado.data_shipment ds
    JOIN avocado.data_shipmentline dsl ON dsl.shipment_id = ds.id
    LEFT JOIN avocado.data_location lo on lo.fsid = ds.origin_id
    LEFT JOIN avocado.data_location ld on ld.fsid = ds.destination_id
    WHERE ds.destination_id = $1
      AND ds.status = 'received'
      AND ds.updated_at > (SELECT submitted_at FROM latest_stock_count)
      AND ds.updated_at <= $2
  ),
  ledger_shipments AS (
    SELECT * FROM shipments_sent
    UNION ALL
    SELECT * FROM shipments_received
  ),
  count_event_batches AS (
    SELECT
      sl.sku,
      d.batch,
      d.amount,
      sc.submitted_at as event_date,
      sc.fs_id
    FROM avocado.data_stockcount sc
    JOIN avocado.data_stockcountline sl ON sl.stock_count_id = sc.id
    JOIN avocado.data_stockcountline_details d ON d.stock_count_line = sl.id
    WHERE sc.fs_id = $1
      AND sc.submitted_at >= (SELECT submitted_at FROM latest_stock_count)
      AND sc.submitted_at <= $2
  ),
  shipments_sent_batches AS (
    SELECT
      dsl.origin_sku as sku,
      dsl_details.batch,
      -dsl_details.amount AS amount,
      coalesce(dss.created_at, ds.created_at) AS event_date,
      ds.origin_id AS fs_id,
      ds.shipment_id
    FROM avocado.data_shipment ds
    JOIN avocado.data_shipmentline dsl ON dsl.shipment_id = ds.id
    JOIN avocado.data_shipmentline_details dsl_details ON dsl_details.shipment_line = dsl.id
    LEFT JOIN avocado.data_shipmentsnapshot dss ON dss.shipment_id = ds.shipment_id AND dss.status = 'sent'
    WHERE ds.origin_id = $1
      AND ds.status IN ('sent', 'arrived', 'received')
      AND coalesce(dss.created_at, ds.created_at) > (SELECT submitted_at FROM latest_stock_count)
      AND coalesce(dss.created_at, ds.created_at) <= $2
  ),
  shipments_received_batches AS (
    SELECT
      dsl.destination_sku as sku,
      dsl_details.batch,
      dsl_details.amount AS amount,
      ds.updated_at AS event_date,
      ds.destination_id AS fs_id,
      ds.shipment_id
    FROM avocado.data_shipment ds
    JOIN avocado.data_shipmentline dsl ON dsl.shipment_id = ds.id
    JOIN avocado.data_shipmentline_details dsl_details ON dsl_details.shipment_line = dsl.id
    LEFT JOIN avocado.data_location lo on lo.fsid = ds.origin_id
    LEFT JOIN avocado.data_location ld on ld.fsid = ds.destination_id
    WHERE ds.destination_id = $1
      AND ds.status = 'received'
      AND ds.updated_at > (SELECT submitted_at FROM latest_stock_count)
      AND ds.updated_at <= $2
  ),
  ledger_shipments_batches AS (
    SELECT * FROM shipments_sent_batches
    UNION ALL
    SELECT * FROM shipments_received_batches
  ),
  /*
    Then we merge with shipments to create a timeline of stock activity.
    We also craft a partiton window using count dates and sum up the totals per sku
  */
  stock_activity AS (
    SELECT sku, event_date, closing, partner_balance, fs_id, event_date AS partition_window
    FROM count_events
    UNION ALL
    SELECT sku, event_date, closing, partner_balance, fs_id,
      (SELECT count_events.event_date 
       FROM count_events 
       WHERE ledger_shipments.event_date > count_events.event_date 
         AND count_events.sku = ledger_shipments.sku -- Added this line to match SKUs
       ORDER BY count_events.event_date DESC 
       LIMIT 1
      ) AS partition_window
    FROM ledger_shipments
  ),
  batch_activity AS (
    SELECT sku, batch, event_date, amount, fs_id, event_date AS partition_window
    FROM count_event_batches
    UNION ALL
    SELECT lsb.sku, lsb.batch, lsb.event_date, lsb.amount, lsb.fs_id,
      (SELECT count_events.event_date 
       FROM count_events 
       WHERE lsb.event_date > count_events.event_date 
         AND count_events.sku = lsb.sku
       ORDER BY count_events.event_date DESC 
       LIMIT 1
      ) AS partition_window
    FROM ledger_shipments_batches lsb
  ),
  running_totals AS (
    SELECT 
      sku,
      event_date,
      SUM(closing) OVER (PARTITION BY partition_window, sku ORDER BY event_date) AS sl_balance,
      SUM(partner_balance) OVER (PARTITION BY partition_window, sku ORDER BY event_date) AS partner_balance,
      fs_id
    FROM stock_activity
  ),
  running_totals_batches AS (
    SELECT 
      sku,
      batch,
      event_date,
      SUM(amount) OVER (PARTITION BY partition_window, sku, batch ORDER BY event_date) AS amount,
      fs_id
    FROM batch_activity
  ),
  final AS (
    SELECT *,
      ROW_NUMBER() OVER (PARTITION BY sku ORDER BY event_date DESC) AS rn
    FROM running_totals
  ),
  final_batches AS (
    SELECT *,
      ROW_NUMBER() OVER (PARTITION BY sku, batch ORDER BY event_date DESC) AS rn
    FROM running_totals_batches
  ),
  ledgers AS (
    SELECT 
      sku,
      event_date AS date,
      sl_balance,
      partner_balance,
      fs_id,
      dp.display_name AS product,
      l.uuid AS location_id
    FROM final
    INNER JOIN avocado.data_productsku dp ON final.sku = dp.old_sku_id
    INNER JOIN avocado.data_location l ON l.fsid = final.fs_id
    WHERE rn = 1
    ORDER BY sku
  ),
  ledgers_batches AS (
    SELECT 
      sku,
      batch,
      event_date AS date,
      amount,
      fs_id,
      l.uuid AS location_id
    FROM final_batches
    INNER JOIN avocado.data_location l ON l.fsid = final_batches.fs_id
    WHERE rn = 1
  ),
  /*
    We get all the shipment_ids used in generating this ledger
  */
  shipment_ids AS (
    SELECT DISTINCT shipment_id FROM ledger_shipments
  )
  /*
    Here we do the final agregating
  */
  SELECT
    base.location_id,
    base.location_fsid,
    (SELECT stock_count_id FROM latest_stock_count) AS base_report,
    (SELECT updated_at FROM latest_stock_count) AS base_report_updated_at,
    (
      SELECT JSON_AGG(shipment_id)
      FROM shipment_ids
    ) AS added_shipments,
    base.updated_at,
    base.stock
  FROM (
    SELECT
      l.location_id,
      dl.fsid AS location_fsid,
      MAX(date) AS updated_at,
      JSON_AGG(JSON_BUILD_OBJECT(
        'sku', l.sku,
        'amount', sl_balance,
        'partner_balance', partner_balance,
        'batches', COALESCE(lb.batches, '[]'::json)
      )) AS stock
    FROM ledgers l
    LEFT JOIN (
      SELECT
        location_id,
        sku,
        JSON_AGG(JSON_BUILD_OBJECT('batch', batch, 'amount', amount)) AS batches
      FROM ledgers_batches
      GROUP BY location_id, sku
    ) lb ON lb.location_id = l.location_id AND lb.sku = l.sku
    LEFT JOIN avocado.data_location dl ON dl.uuid = l.location_id
    INNER JOIN avocado.data_productsku ps 
      ON ps.old_sku_id = l.sku 
     AND NOT ps._deleted 
     AND ps.market_id = dl.market_id
    GROUP BY l.location_id, dl.fsid
  ) AS base
`

module.exports = calculateLocationLedgerQuery
